import React, { useEffect, useRef, useState } from "react";
import { Alert, Box, Button, CircularProgress, LinearProgress, Typography } from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import { saveAs } from "file-saver";
import { fetchArticleImportLookups, fetchNextArticleImportBarcode } from "./articleImportLookups";
import { buildArticleImportTemplate } from "./articleImportTemplate";
import ArticleImportPreview from "./ArticleImportPreview";
import ArticleImportDropZone from "./ArticleImportDropZone";
import { fetchArticleImportSaveStatus } from "./articleImportValidationService";
import { Check, FileSpreadsheet, FolderUp, ShieldCheck } from "lucide-react";
import "./ArticleImportPage.scss";

export default function ImportimiArtikujve() {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");
  const [downloaded, setDownloaded] = useState(false);
  const [showInPos, setShowInPos] = useState(true);
  const [rows, setRows] = useState([]);
  const [lookups, setLookups] = useState(null);
  const [filename, setFilename] = useState("");
  const [importId, setImportId] = useState("");
  const [saveEnabled, setSaveEnabled] = useState(false);
  const [previewVersion, setPreviewVersion] = useState(0);
  const [loading, setLoading] = useState(false);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [stage, setStage] = useState("");
  const generation = useRef(0);
  const job = useRef(null);
  const uploadActive = useRef(false);
  useEffect(() => () => {
    generation.current++;
    job.current?.worker.terminate();
    job.current?.reject(new Error("Ngarkimi u anulua."));
  }, []);

  const cancelUpload = () => {
    generation.current++;
    job.current?.worker.terminate();
    job.current?.reject(new Error("Ngarkimi u anulua."));
    job.current = null;
    setLoading(false);
    uploadActive.current = false;
    setStage("");
  };

  const uploadWorkbook = async file => {
    if (!file || uploadActive.current || downloading || previewBusy) return;
    setLoadError("");
    if (!/\.xlsx$/i.test(file.name)) {
      setLoadError("Zgjidhni një skedar .xlsx të plotësuar sipas shabllonit të ri.");
      return;
    }
    if (file.size > 100 * 1024 * 1024) {
      setLoadError("Skedari tejkalon 100 MB. Ndajeni në disa skedarë më të vegjël.");
      return;
    }
    const request = ++generation.current;
    uploadActive.current = true;
    setLoading(true);
    setStage("Duke ngarkuar skedarin dhe listat aktuale nga Accountree…");
    try {
      const [buffer, currentLookups] = await Promise.all([
        file.arrayBuffer().catch(() => { throw new Error(`Skedari “${file.name}” nuk u lexua nga pajisja. Zgjidheni përsëri ose ruajeni një kopje të re.`); }),
        fetchArticleImportLookups(),
      ]);
      if (request !== generation.current) return;
      const enabled = await fetchArticleImportSaveStatus().catch(() => false);
      const fileId = window.crypto.randomUUID();
      if (request !== generation.current) return;
      const imported = await new Promise((resolve, reject) => {
        const worker = new Worker(new URL("./articleImport.worker.js", import.meta.url));
        job.current = { worker, reject };
        worker.onmessage = message => {
          if (request !== generation.current) return;
          if (message.data.stage) setStage(message.data.stage);
          else if (message.data.needsBarcode) {
            setStage("Duke marrë serinë ACC të kompanisë…");
            fetchNextArticleImportBarcode().then(nextBarcode => {
              if (request === generation.current) worker.postMessage({ nextBarcode });
            }).catch(reject);
          }
          else if (message.data.error) reject(new Error(message.data.error));
          else if (message.data.rows) resolve(message.data.rows);
        };
        worker.onerror = () => reject(new Error("Përpunimi i Excel-it u ndërpre papritur. Ringarkoni faqen dhe provoni përsëri; për skedarë të mëdhenj, ndajeni në pjesë më të vogla."));
        worker.onmessageerror = () => reject(new Error("Rezultati i Excel-it nuk u transferua në faqe. Provoni përsëri me një skedar më të vogël."));
        worker.postMessage({ buffer, lookups: currentLookups }, [buffer]);
      });
      if (request !== generation.current) return;
      setLookups(currentLookups);
      setRows(imported);
      setFilename(file.name);
      setImportId(fileId); setSaveEnabled(enabled);
      setPreviewVersion(value => value + 1);
    } catch (err) {
      if (request === generation.current) setLoadError(err.message || "Excel-i nuk u lexua.");
    } finally {
      if (request === generation.current) {
        job.current?.worker.terminate();
        job.current = null;
        setLoading(false);
        uploadActive.current = false;
        setStage("");
      }
    }
  };
  const downloadTemplate = async () => {
    setDownloading(true);
    setError("");
    setDownloaded(false);
    try {
      const lookups = await fetchArticleImportLookups();
      const buffer = await buildArticleImportTemplate(lookups).xlsx.writeBuffer();
      saveAs(new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }), "Accountree-Shablloni-Artikujve.xlsx");
      setDownloaded(true);
    } catch (err) {
      setError(err.message || "Shablloni nuk u krijua. Provoni përsëri.");
    } finally {
      setDownloading(false);
    }
  };
  return (
    <div className="Crud__Container article-import-page">
      <header className="article-import-page__header">
        <div className="article-import-page__identity">
          <span className="article-import-page__icon"><FileSpreadsheet size={25} /></span>
          <div><h1>Importimi i artikujve</h1><p>Ngarkoni Excel-in, kontrolloni artikujt dhe caktoni njësitë organizative.</p></div>
        </div>
        <span className="article-import-page__status"><ShieldCheck size={15} />{saveEnabled ? "Kontroll dhe ruajtje" : "Përgatitje · pa ruajtje"}</span>
      </header>

      <div className="article-import-page__steps" aria-label="Hapat e importimit">
        <div className={`article-import-page__step ${downloaded || filename ? "article-import-page__step--done" : downloaded ? "article-import-page__step--active" : ""}`}><span>1</span>Përgatitni Excel-in</div>
        <div className={`article-import-page__step ${filename ? "article-import-page__step--done" : downloaded ? "article-import-page__step--active" : ""}`}><span>{filename ? <Check size={15} /> : "2"}</span>Ngarkoni skedarin</div>
        <div className={`article-import-page__step ${filename ? "article-import-page__step--active" : ""}`}><span>3</span>Kontrolloni artikujt</div>
      </div>

      <div className="article-import-page__preparation">
        <section className="article-import-page__card article-import-page__template">
          <span className="article-import-page__card-icon"><FileSpreadsheet size={23} /></span>
          <div className="article-import-page__card-copy"><h2>Shablloni Excel</h2><p>Listat dhe udhëzimet e plotësimit gjenden brenda skedarit.</p></div>
          <Button variant="outlined" onClick={downloadTemplate} disabled={downloading || loading || previewBusy}
            startIcon={downloading ? <CircularProgress size={16} color="inherit" /> : <DownloadOutlinedIcon />}>
            {downloading ? "Duke përgatitur…" : "Shkarko shabllonin"}
          </Button>
        </section>
        <ArticleImportDropZone disabled={loading || downloading || previewBusy} loaded={Boolean(filename)} onFile={uploadWorkbook} onError={setLoadError}>
          <span className="article-import-page__card-icon"><FolderUp size={23} /></span>
          <div className="article-import-page__card-copy"><h2>{filename ? "Skedari u ngarkua" : "Ngarkoni Excel-in"}</h2>
            <p title={filename || undefined}>{filename || "Zgjidhni shabllonin e plotësuar · .xlsx, deri në 100 MB"}</p></div>
          <Button component="label" variant="contained" disableElevation startIcon={<UploadFileOutlinedIcon />}
            disabled={loading || downloading || previewBusy}>
            {filename ? "Ndrysho skedarin" : "Ngarko Excel"}
            <input hidden type="file" accept=".xlsx" onChange={event => {
              const file = event.target.files?.[0];
              event.target.value = "";
              uploadWorkbook(file);
            }} disabled={loading || downloading || previewBusy} />
          </Button>
        </ArticleImportDropZone>
      </div>
      {error && <Alert severity="error" role="alert">{error}</Alert>}
      {downloaded && <Alert severity="success">Shablloni u përgatit për shkarkim. Udhëzimet gjenden në Excel.</Alert>}
      {loadError && <Alert severity="error" role="alert"><strong>Ngarkimi nuk përfundoi.</strong> {loadError}{filename && " Tabela e skedarit të mëparshëm mbetet e hapur."}</Alert>}
      {loading && <section className="article-import-page__loading">
        <div><Typography role="status" variant="body2">{stage}</Typography><Button size="small" onClick={cancelUpload}>Anulo ngarkimin</Button></div>
        <LinearProgress />
      </section>}

      {lookups ? <section className="article-import-page__workspace">
        <div className="article-import-page__workspace-header">
          <div><h2>Kontrolloni artikujt</h2><p>Editoni të dhënat dhe shtoni njësitë para ruajtjes.</p></div>

        </div>
        <Box component="fieldset" disabled={loading} sx={{ opacity: loading ? 0.6 : 1, border: 0, p: 0, m: 0, minWidth: 0 }}>
          <ArticleImportPreview key={previewVersion} rows={rows} setRows={setRows} lookups={lookups} showInPos={showInPos} onShowInPosChange={setShowInPos} onBusyChange={setPreviewBusy} importId={importId} saveEnabled={saveEnabled} />
        </Box>
        <div className="article-import-page__footer"><ShieldCheck size={16} />{saveEnabled ? "Artikujt ruhen vetëm kur klikoni Ruaj artikujt. Kontrolloni raportin për rreshtat me gabime." : "Ruajtja në databazë ende nuk është aktivizuar."}</div>
      </section> : !loading && <section className="article-import-page__empty">
        <span><FileSpreadsheet size={34} /></span><h2>Artikujt tuaj do të shfaqen këtu</h2>
        <p>Shkarkoni shabllonin, plotësojeni dhe ngarkojeni për të filluar kontrollin.</p>
        <div><ShieldCheck size={15} /> Ngarkimi nuk ruan artikuj në databazë.</div>
      </section>}
    </div>
  );
}
